import { useEffect, useState } from "react";
import { api, formatApiError, formatFCFA } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Trash2, TrendingUp, TrendingDown, Wallet, Pencil, Target, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const CATEGORIES_IN = [
  { value: "cotisation", label: "Cotisations" },
  { value: "don", label: "Dons" },
  { value: "subvention", label: "Subventions" },
  { value: "sponsoring", label: "Sponsoring" },
  { value: "billetterie", label: "Billetterie" },
  { value: "autre_entree", label: "Autres entrées" },
];
const CATEGORIES_OUT = [
  { value: "salaire", label: "Salaires" },
  { value: "materiel", label: "Matériel" },
  { value: "deplacement", label: "Déplacement" },
  { value: "restauration", label: "Restauration" },
  { value: "autre_sortie", label: "Autres sorties" },
];
const ALL_CAT_LABELS = Object.fromEntries(
  [...CATEGORIES_IN, ...CATEGORIES_OUT].map((c) => [c.value, c.label])
);

const currentYear = new Date().getFullYear();
const EMPTY = { year: currentYear, type: "entree", category: "cotisation", forecast_amount: "", notes: "" };

export default function Budget() {
  const [year, setYear] = useState(currentYear);
  const [items, setItems] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [i, c] = await Promise.all([
      api.get(`/budget?year=${year}`),
      api.get(`/budget/comparison?year=${year}`),
    ]);
    setItems(i.data);
    setComparison(c.data);
  };
  useEffect(() => { load(); }, [year]);

  const startCreate = () => { setEditing(null); setForm({ ...EMPTY, year }); setOpen(true); };
  const startEdit = (i) => { setEditing(i); setForm({ ...EMPTY, ...i }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, year: Number(form.year), forecast_amount: Number(form.forecast_amount) };
      if (editing) {
        await api.patch(`/budget/${editing.id}`, payload);
      } else {
        await api.post("/budget", payload);
      }
      toast.success("Ligne budgétaire enregistrée");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette ligne ?")) return;
    await api.delete(`/budget/${id}`);
    load();
  };

  const downloadReport = async (detailed) => {
    try {
      const res = await api.get(`/reports/annual?year=${year}&detailed=${detailed}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bilan_${detailed ? "detaille" : "synthetique"}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so the browser has time to start the download
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Bilan généré");
    } catch (e) { toast.error("Erreur lors de la génération"); }
  };

  if (!comparison) return <div className="p-8 text-slate-400">Chargement…</div>;

  const rowsIn = comparison.rows.filter((r) => r.type === "entree");
  const rowsOut = comparison.rows.filter((r) => r.type === "sortie");

  const chartData = comparison.rows.map((r) => ({
    name: ALL_CAT_LABELS[r.category] || r.category,
    Prévu: r.forecast,
    Réalisé: r.realized,
    type: r.type,
  }));

  const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

  return (
    <div data-testid="budget-page">
      <PageHeader title="Budget prévisionnel"
        subtitle="Comparaison entre prévisions annuelles et opérations réalisées">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32" data-testid="year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => downloadReport(false)} variant="outline" data-testid="export-bilan-synth-button">
          <FileDown className="h-4 w-4 mr-2" /> Bilan synthétique
        </Button>
        <Button onClick={() => downloadReport(true)} variant="outline" data-testid="export-bilan-detail-button">
          <FileDown className="h-4 w-4 mr-2" /> Bilan détaillé
        </Button>
        <Button onClick={startCreate} data-testid="add-budget-button"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle ligne
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8 space-y-6">
        {/* Totals */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TotalCard icon={TrendingUp} label="Entrées" color="emerald"
            forecast={comparison.totals.forecast_income}
            realized={comparison.totals.realized_income}
            testid="total-income" />
          <TotalCard icon={TrendingDown} label="Sorties" color="amber"
            forecast={comparison.totals.forecast_expense}
            realized={comparison.totals.realized_expense}
            lowerIsBetter
            testid="total-expense" />
          <TotalCard icon={Wallet} label="Solde net" color={comparison.totals.realized_balance >= 0 ? "emerald" : "red"}
            forecast={comparison.totals.forecast_balance}
            realized={comparison.totals.realized_balance}
            testid="total-balance" />
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="budget-chart">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Prévu vs Réalisé</div>
            <h3 className="font-barlow font-bold text-xl tracking-tight mb-4">Comparaison par catégorie — {year}</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} angle={-25} textAnchor="end" />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatFCFA(v)}
                    contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Prévu" fill="#94A3B8" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Réalisé" fill="#00A859" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Comparison tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ComparisonTable title="Entrées" rows={rowsIn} year={year} items={items.filter((i) => i.type === "entree")}
            onEdit={startEdit} onDel={del} testid="income-table" />
          <ComparisonTable title="Sorties" rows={rowsOut} year={year} items={items.filter((i) => i.type === "sortie")}
            onEdit={startEdit} onDel={del} lowerIsBetter testid="expense-table" />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">
              {editing ? "Modifier la ligne" : "Nouvelle ligne budgétaire"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Année" type="number" value={form.year}
              onChange={(v) => setForm({ ...form, year: v })} testid="budget-year" />
            <SelectField label="Type" value={form.type}
              onChange={(v) => setForm({ ...form, type: v, category: v === "entree" ? "cotisation" : "salaire" })}
              options={[
                { value: "entree", label: "Entrée" },
                { value: "sortie", label: "Sortie" },
              ]} testid="budget-type" />
            <div className="col-span-2">
              <SelectField label="Catégorie" value={form.category}
                onChange={(v) => setForm({ ...form, category: v })}
                options={form.type === "entree" ? CATEGORIES_IN : CATEGORIES_OUT}
                testid="budget-category" />
            </div>
            <div className="col-span-2">
              <Field label="Montant prévu (FCFA)" type="number" value={form.forecast_amount}
                onChange={(v) => setForm({ ...form, forecast_amount: v })} required testid="budget-amount" />
            </div>
            <div className="col-span-2">
              <Field label="Notes (optionnel)" value={form.notes}
                onChange={(v) => setForm({ ...form, notes: v })} testid="budget-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-budget-button" className="bg-emerald-600 hover:bg-emerald-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TotalCard({ icon: Icon, label, color, forecast, realized, testid, lowerIsBetter = false }) {
  const palettes = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    red: "text-red-700 bg-red-50 border-red-200",
  };
  const pct = forecast > 0 ? Math.round((realized / forecast) * 100) : null;
  // Color logic depends on whether higher or lower realized is better.
  // For income/balance: higher is better → ≥100% green, ≥70% amber, else red
  // For expense: lower is better → ≤100% green, ≤120% amber, else red
  const pctColor = pct === null ? "text-slate-500"
    : lowerIsBetter
      ? (pct <= 100 ? "text-emerald-600" : pct <= 120 ? "text-amber-600" : "text-red-600")
      : (pct >= 100 ? "text-emerald-600" : pct >= 70 ? "text-amber-600" : "text-red-600");
  return (
    <div data-testid={testid} className="stat-card bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <span className={`p-2 rounded-md border ${palettes[color]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="font-barlow font-bold text-2xl tracking-tight text-slate-900 leading-none">
        {formatFCFA(realized)}
      </div>
      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
        <Target className="h-3 w-3" />
        Prévu : {formatFCFA(forecast)}
        {pct !== null && (
          <span className={`ml-1 font-semibold ${pctColor}`}>
            ({pct}%)
          </span>
        )}
      </div>
    </div>
  );
}

function ComparisonTable({ title, rows, items, year, onEdit, onDel, testid, lowerIsBetter = false }) {
  const badgeClass = (pct) =>
    lowerIsBetter
      ? (pct <= 100 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
         : pct <= 120 ? "bg-amber-100 text-amber-700 border-amber-200"
         : "bg-red-100 text-red-700 border-red-200")
      : (pct >= 100 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
         : pct >= 70 ? "bg-amber-100 text-amber-700 border-amber-200"
         : "bg-red-100 text-red-700 border-red-200");
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{year}</div>
          <h3 className="font-barlow font-bold text-lg tracking-tight">{title}</h3>
        </div>
      </div>
      <table className="w-full data-table text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
            <th className="text-left px-4 py-2.5">Catégorie</th>
            <th className="text-right px-4 py-2.5">Prévu</th>
            <th className="text-right px-4 py-2.5">Réalisé</th>
            <th className="text-right px-4 py-2.5">Écart</th>
            <th className="text-right px-4 py-2.5">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan="5" className="p-8 text-center text-slate-400">Aucune ligne</td></tr>
          ) : rows.map((r) => (
            <tr key={`${r.type}-${r.category}`} className="border-b border-slate-100">
              <td className="px-4 py-3 text-slate-900">{ALL_CAT_LABELS[r.category] || r.category}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-600">{formatFCFA(r.forecast)}</td>
              <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{formatFCFA(r.realized)}</td>
              <td className={`px-4 py-3 text-right font-mono ${
                lowerIsBetter
                  ? (r.variance <= 0 ? "text-emerald-700" : "text-red-700")
                  : (r.variance >= 0 ? "text-emerald-700" : "text-red-700")
              }`}>
                {r.variance >= 0 ? "+" : ""}{formatFCFA(r.variance)}
              </td>
              <td className="px-4 py-3 text-right">
                <Badge className={badgeClass(r.percent)}>
                  {r.percent}%
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Editable budget lines */}
      {items.length > 0 && (
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Lignes prévisionnelles</div>
          <div className="space-y-1">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-2 text-xs">
                <span className="flex-1 capitalize">{ALL_CAT_LABELS[i.category] || i.category}</span>
                <span className="font-mono">{formatFCFA(i.forecast_amount)}</span>
                <Button size="sm" variant="ghost" onClick={() => onEdit(i)}
                  className="h-6 w-6 p-0" data-testid={`edit-budget-${i.id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDel(i.id)}
                  className="h-6 w-6 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-budget-${i.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
